import { useCallback, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { alerts } from '../../../shared/ui/Alerts';
import { ChatDocument } from '../../../domain/documents/models/ChatDocument';
import { FileStatusDto } from '../../../services/providers/progpt/dtos/responses/ProGPTDocumentDto';
import {
    setDocuments,
    setIsImporting,
    setSelectedDocuments,
    clearSelectedDocuments,
} from '../../../redux/features/chatDocuments/chatDocumentsSlice';
import { shallowEqual } from 'react-redux';
import { appendMessages, updateMessageContent } from '../../../redux/features/chatMessages/chatMessagesSlice';
import { AuthorRoles, ChatMessage, ChatMessageType } from '../../../domain/messages/models/ChatMessage';
import { UploadStatus } from '../components/UploadStatusIndicator';
import { formatFileSize } from '../../utils/TextUtils';

export type FileUploadStatus = 'uploading' | 'success' | 'error';

export interface UseDocumentsVm {
    documents: ChatDocument[];
    isImporting: number;
    uploadStatus: UploadStatus;
    fileUploadStatuses: Map<string, FileUploadStatus>;
    selectedDocumentIds: Set<string>;
    isDeleting: boolean;
    onUploadDocuments: (files: File[]) => Promise<void>;
    onFetchDocuments: () => Promise<void>;
    onDeleteDocuments: (documentIds: string[]) => Promise<void>;
    onSelectDocument: (documentId: string, selected: boolean) => void;
    onSelectAllDocuments: () => void;
    onClearSelection: () => void;
}

const MAX_BATCH_SIZE_BYTES = 20 * 1024 * 1024; // 20MB
const MAX_PARALLEL_LARGE_FILES = 3;

export const useProGPTDocuments = (sessionId: string): UseDocumentsVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const [isDeleting, setIsDeleting] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
    const [fileUploadStatuses, setFileUploadStatuses] = useState<Map<string, FileUploadStatus>>(new Map());

    const documents = useAppSelector((state) => state.documents.bySessionId[sessionId] ?? [], shallowEqual);
    const isImporting = useAppSelector((state) => state.documents.isImporting[sessionId] ?? 0);
    const selectedDocumentIdsArray = useAppSelector(
        (state) => state.documents.selectedDocumentIds[sessionId] ?? [],
        shallowEqual
    );
    const selectedDocumentIds = useMemo(() => new Set(selectedDocumentIdsArray), [selectedDocumentIdsArray]);

    const onFetchDocuments = useCallback(async (): Promise<void> => {
        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            const fetchedDocuments = await providerServices.proGPT.documents.getDocumentsAsync(sessionId, accessToken);
            dispatch(setDocuments({ sessionId, documents: fetchedDocuments }));
        } catch (error) {
            alerts.error('Failed to fetch documents. Please try again.');
            console.error('Failed to fetch documents:', error);
        }
    }, [dispatch, instance, providerServices.proGPT.documents, sessionId]);

    const sendBatchedRequests = useCallback(
        async (
            files: File[],
            onFileComplete: (name: string, status: FileUploadStatus) => void,
        ): Promise<void> => {
            // Per-file status map: tracks current status of each file during upload
            const fileStatusMap = new Map<string, { status: FileUploadStatus; sizeStr: string; fileStatus: FileStatusDto[] }>(
                files.map((f) => [f.name, { status: 'uploading' as FileUploadStatus, sizeStr: formatFileSize(f.size), fileStatus: [] }]),
            );

            const buildContent = () =>
                JSON.stringify({
                    documents: files.map((f) => {
                        const entry = fileStatusMap.get(f.name);
                        const status = entry?.status ?? 'uploading';
                        return {
                            name: f.name,
                            size: entry?.sizeStr ?? '',
                            isUploaded: status === 'success',
                            isProgressMessage: status === 'uploading',
                            uploadPercentage: status === 'uploading' ? 0.5 : 1,
                            fileStatus: entry?.fileStatus ?? [],
                        };
                    }),
                });

            const uploadMessageId = crypto.randomUUID();
            const optimisticBotFileUploadMessage: ChatMessage = {
                id: uploadMessageId,
                chatId: sessionId,
                type: ChatMessageType.Document,
                timestamp: Date.now(),
                userName: 'bot',
                content: buildContent(),
                authorRole: AuthorRoles.Bot,
            };

            dispatch(appendMessages({ sessionId, messages: [optimisticBotFileUploadMessage] }));

            const sortedFiles = [...files].sort((a, b) => a.size - b.size);
            const smallFiles: File[] = [];
            const largeFiles: File[] = [];

            sortedFiles.forEach((file) => {
                if (file.size <= MAX_BATCH_SIZE_BYTES) {
                    smallFiles.push(file);
                } else {
                    largeFiles.push(file);
                }
            });

            const batches: File[][] = [];
            let currentBatch: File[] = [];
            let currentBatchSize = 0;

            for (const file of smallFiles) {
                if (currentBatchSize + file.size <= MAX_BATCH_SIZE_BYTES) {
                    currentBatch.push(file);
                    currentBatchSize += file.size;
                } else {
                    batches.push(currentBatch);
                    currentBatch = [file];
                    currentBatchSize = file.size;
                }
            }
            if (currentBatch.length > 0) batches.push(currentBatch);

            const accessToken = await acquireApiAccessTokenAsync({ msalInstance: instance });

            let hasErrors = false;

            const processBatch = async (batch: File[]) => {
                const results = await Promise.allSettled(
                    batch.map((file) =>
                        providerServices.proGPT.documents.uploadDocumentsAsync(
                            { chatId: sessionId, files: [file] },
                            accessToken,
                        ),
                    ),
                );

                results.forEach((result, index) => {
                    const file = batch[index];
                    const entry = fileStatusMap.get(file.name)!;
                    if (result.status === 'rejected') {
                        entry.status = 'error';
                        entry.fileStatus = [{ status: 'Failed', fileName: file.name, details: String(result.reason), eventType: '', timeStamp: '', chatId: '', fileLength: 0, files: [], docId: '', indexId: '' }];
                        hasErrors = true;
                        console.error(`Failed to upload ${file.name}:`, result.reason);
                    } else if (!result.value.isSuccess) {
                        entry.status = 'error';
                        entry.fileStatus = result.value.value?.fileStatus?.filter((fs) => fs.fileName === file.name) ?? [];
                        hasErrors = true;
                        console.error(`Upload rejected by backend for ${file.name}:`, result.value.errors);
                    } else {
                        const fileStatuses = result.value.value?.fileStatus?.filter(
                            (fs) => fs.fileName === file.name,
                        ) ?? [];
                        entry.fileStatus = fileStatuses;
                        // success only when all steps in fileStatus report Success
                        const hasFailure = fileStatuses.some((fs) => fs.status !== 'Success');
                        const allSuccess = fileStatuses.length > 0 && !hasFailure;
                        if (hasFailure) {
                            entry.status = 'error';
                            hasErrors = true;
                        } else if (allSuccess) {
                            entry.status = 'success';
                        } else {
                            // no fileStatus entries yet — treat as success (upload accepted)
                            entry.status = 'success';
                        }
                    }
                    onFileComplete(file.name, entry.status);
                });

                dispatch(updateMessageContent({ sessionId, messageId: uploadMessageId, content: buildContent() }));
            };

            for (const batch of batches) {
                await processBatch(batch);
            }

            for (let i = 0; i < largeFiles.length; i += MAX_PARALLEL_LARGE_FILES) {
                await processBatch(largeFiles.slice(i, i + MAX_PARALLEL_LARGE_FILES));
            }

            if (hasErrors) {
                throw new Error('One or more file uploads failed');
            }
        },
        [dispatch, instance, providerServices.proGPT.documents, sessionId],
    );

    const onUploadDocuments = useCallback(
        async (files: File[]): Promise<void> => {
            if (files.length === 0) return;

            setFileUploadStatuses(new Map(files.map((f) => [f.name, 'uploading'])));
            dispatch(setIsImporting({ sessionId, count: files.length }));
            setUploadStatus('uploading');

            try {
                await sendBatchedRequests(files, (name, status) => {
                    setFileUploadStatuses((prev) => new Map(prev).set(name, status));
                });
                alerts.success('Documents uploaded successfully.');
                setUploadStatus('success');
            } catch (error) {
                alerts.error('One or more files failed to upload. Please try again.');
                console.error('Failed to upload documents:', error);
                setUploadStatus('error');
            } finally {
                dispatch(setIsImporting({ sessionId, count: 0 }));
                // Always refresh so successfully uploaded files appear in the list
                await onFetchDocuments();
                // Keep only failed entries so they remain visible as red bars; clear the rest
                setFileUploadStatuses((prev) => {
                    const next = new Map(prev);
                    for (const [name, status] of next) {
                        if (status !== 'error') next.delete(name);
                    }
                    return next;
                });
            }
        },
        [dispatch, onFetchDocuments, sendBatchedRequests, sessionId],
    );

    const onDeleteDocuments = useCallback(
        async (documentIds: string[]): Promise<void> => {
            if (documentIds.length === 0) return;

            setIsDeleting(true);

            // Failed in-progress items were never persisted — remove them locally only
            const failedNames = documentIds
                .filter((id) => id.startsWith('in-progress-'))
                .map((id) => id.slice('in-progress-'.length));
            const realDocumentIds = documentIds.filter((id) => !id.startsWith('in-progress-'));

            try {
                if (failedNames.length > 0) {
                    setFileUploadStatuses((prev) => {
                        const next = new Map(prev);
                        failedNames.forEach((name) => next.delete(name));
                        return next;
                    });
                }

                if (realDocumentIds.length > 0) {
                    const accessToken = await acquireApiAccessTokenAsync({ msalInstance: instance });
                    await providerServices.proGPT.documents.deleteDocumentsAsync(
                        { chatId: sessionId, fileIds: realDocumentIds },
                        accessToken,
                    );
                    await onFetchDocuments();
                }

                alerts.success('Documents deleted successfully.');
                dispatch(clearSelectedDocuments(sessionId));
            } catch (error) {
                alerts.error('Failed to delete documents. Please try again.');
                console.error('Failed to delete documents:', error);
            } finally {
                setIsDeleting(false);
            }
        },
        [dispatch, instance, onFetchDocuments, providerServices.proGPT.documents, sessionId],
    );

    const onSelectDocument = useCallback(
        (documentId: string, selected: boolean): void => {
            const newSelection = new Set(selectedDocumentIds);
            if (selected) {
                newSelection.add(documentId);
            } else {
                newSelection.delete(documentId);
            }
            dispatch(setSelectedDocuments({ sessionId, documentIds: Array.from(newSelection) }));
        },
        [dispatch, selectedDocumentIds, sessionId],
    );

    const onSelectAllDocuments = useCallback((): void => {
        const docIds = documents.filter((doc) => !doc.id.startsWith('in-progress')).map((doc) => doc.id);
        const failedIds = Array.from(fileUploadStatuses.entries())
            .filter(([, status]) => status === 'error')
            .map(([name]) => `in-progress-${name}`);
        dispatch(setSelectedDocuments({ sessionId, documentIds: [...docIds, ...failedIds] }));
    }, [dispatch, documents, fileUploadStatuses, sessionId]);

    const onClearSelection = useCallback((): void => {
        dispatch(clearSelectedDocuments(sessionId));
    }, [dispatch, sessionId]);

    // useEffect(() => {
    //     void onFetchDocuments();
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [sessionId]);

    return {
        documents,
        isImporting,
        uploadStatus,
        fileUploadStatuses,
        selectedDocumentIds,
        isDeleting,
        onUploadDocuments,
        onFetchDocuments,
        onDeleteDocuments,
        onSelectDocument,
        onSelectAllDocuments,
        onClearSelection,
    };
};